import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AudioLines,
  CheckCircle2,
  Clock,
  Loader2,
  Trash2,
  TriangleAlert,
  Upload,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import ChoixActivite from '../../components/assistants/ChoixActivite';
import EnregistreurReunion from '../../components/assistants/EnregistreurReunion';
import RelectureReunion from '../../components/assistants/RelectureReunion';
import {
  assistantsService,
  type EtatReunion,
  type Projet,
  type Reunion,
} from '../../services/api/assistants';
import { alleger, enOctetsLisibles } from '../../utils/compresserAudio';

/** Un travail en cours est réinterrogé à ce rythme : assez pour suivre, pas pour saturer. */
const CADENCE = 5000;

const ETATS: Record<EtatReunion, { libelle: string; classe: string }> = {
  deposee: { libelle: 'En attente', classe: 'bg-[#F0F0EC] text-eg-gris' },
  en_traitement: { libelle: 'En cours', classe: 'bg-[#FBEAB4] text-[#7A5C00]' },
  a_relire: { libelle: 'À relire', classe: 'bg-eg-voile text-eg-ombre' },
  validee: { libelle: 'Versée', classe: 'bg-[#EEF2ED] text-[#1F5C45]' },
  echouee: { libelle: 'Échouée', classe: 'bg-[#FBF1F1] text-[#8A2A2A]' },
};

/**
 * Réunions : dépôt d'un enregistrement, puis relecture du compte rendu.
 *
 * Le dépôt ne rend pas le compte rendu. Une réunion d'une heure demande
 * plusieurs minutes de transcription, on suit donc l'avancement plutôt que
 * d'attendre une réponse qui n'arriverait pas.
 *
 * L'activité est exigée au dépôt : c'est elle qui décide où ira le compte
 * rendu, et sans elle il n'alimente aucune mémoire.
 */
const ReunionsPage: React.FC = () => {
  const [projets, setProjets] = useState<Projet[]>([]);
  const [reunions, setReunions] = useState<Reunion[]>([]);
  const [ouverte, setOuverte] = useState<Reunion | null>(null);
  const [chargement, setChargement] = useState(true);

  const [source, setSource] = useState<'micro' | 'fichier'>('micro');
  const [fichier, setFichier] = useState<File | null>(null);
  // Remonte l'enregistreur après un dépôt : sans quoi la réunion précédente
  // resterait proposée à la réécoute, et on la déposerait deux fois.
  const [remise, setRemise] = useState(0);
  const [projet, setProjet] = useState<number | string | null>(null);
  const [titre, setTitre] = useState('');
  // Une réunion se dépose le jour où elle se tient, ou le lendemain. Proposer
  // la date du jour évite une saisie dans l'immense majorité des cas, et reste
  // corrigeable.
  const [tenueLe, setTenueLe] = useState(() => new Date().toISOString().slice(0, 10));

  // Plan de compte rendu. La question est posée avant le dépôt : un service
  // qui a son propre plan doit le retrouver, et le compte rendu se rédige
  // dès la fin de la transcription, sans attendre personne.
  const [avecPlan, setAvecPlan] = useState<'non' | 'texte' | 'fichier'>('non');
  const [plan, setPlan] = useState('');
  const [planFichier, setPlanFichier] = useState<File | null>(null);
  const dernierPlan = (() => {
    try { return localStorage.getItem('omnia.dernier_plan_cr') || ''; } catch { return ''; }
  })();

  // Réunion dont la suppression est proposée : demander confirmation dans la
  // ligne plutôt que dans une boîte de dialogue, qu'on referme sans lire.
  const [aSupprimer, setASupprimer] = useState<number | null>(null);
  const [suppression, setSuppression] = useState(false);

  const [etape, setEtape] = useState('');
  const [progres, setProgres] = useState(0);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const champ = useRef<HTMLInputElement | null>(null);

  const recharger = useCallback(async () => {
    const liste = await assistantsService.listerReunions();
    setReunions(liste);
    return liste;
  }, []);

  useEffect(() => {
    Promise.all([assistantsService.listerProjets().then(setProjets), recharger()])
      .catch(() => setErreur("Les réunions n'ont pas pu être chargées."))
      .finally(() => setChargement(false));
  }, [recharger]);

  // Tant qu'un enregistrement est en cours de traitement, l'écran se tient à
  // jour tout seul : l'agent a déposé et est passé à autre chose.
  useEffect(() => {
    const enCours = reunions.some((r) => r.etat === 'deposee' || r.etat === 'en_traitement');
    if (!enCours) return undefined;
    const minuteur = setInterval(() => { recharger().catch(() => {}); }, CADENCE);
    return () => clearInterval(minuteur);
  }, [reunions, recharger]);

  const ouvrir = async (reunion: Reunion) => {
    setErreur('');
    try {
      setOuverte(await assistantsService.chargerReunion(reunion.id));
    } catch (e: any) {
      setErreur(e.message || "La réunion n'a pas pu être ouverte.");
    }
  };

  const supprimer = async (id: number) => {
    setSuppression(true);
    setErreur('');
    try {
      await assistantsService.supprimerReunion(id);
      setASupprimer(null);
      if (ouverte?.id === id) setOuverte(null);
      await recharger();
    } catch (e: any) {
      setErreur(e.message || "La réunion n'a pas pu être supprimée.");
    } finally {
      setSuppression(false);
    }
  };

  const deposer = async () => {
    if (!fichier) return;
    // Même garde-fou que le serveur, mais avant l'envoi : inutile de faire
    // patienter quelqu'un sur un téléversement voué à être refusé.
    if (fichier.size < 2 * 1024) {
      setErreur(
        "Ce fichier ne contient pas de son : il est vide ou incomplet. "
        + "Vérifiez l'enregistrement avant de le déposer.",
      );
      return;
    }
    setEnvoi(true);
    setErreur('');
    setProgres(0);

    try {
      setEtape("Allègement de l'enregistrement");
      const allegement = await alleger(fichier);
      setEtape(
        allegement.allege
          ? `Envoi de ${enOctetsLisibles(allegement.octetsEnvoyes)} `
            + `au lieu de ${enOctetsLisibles(allegement.octetsOrigine)}`
          : `Envoi de ${enOctetsLisibles(allegement.octetsEnvoyes)}`,
      );

      const deposee = await assistantsService.deposerReunion(
        allegement.fichier,
        typeof projet === 'string' ? projet.trim() || null : projet,
        {
          titre: titre.trim(),
          tenue_le: tenueLe || undefined,
          plan: avecPlan === 'texte' ? plan.trim() : undefined,
          planFichier: avecPlan === 'fichier' ? planFichier : null,
        },
        setProgres,
      );

      if (avecPlan === 'texte' && plan.trim()) {
        try { localStorage.setItem('omnia.dernier_plan_cr', plan.trim()); } catch { /* sans effet */ }
      }
      setFichier(null);
      setTitre('');
      setTenueLe(new Date().toISOString().slice(0, 10));
      setRemise((n) => n + 1);
      if (champ.current) champ.current.value = '';
      setEtape('Transcription en cours');
      await recharger();
      suivre(deposee.tache);
    } catch (e: any) {
      setErreur(e.message || "L'enregistrement n'a pas pu être déposé.");
      setEtape('');
    } finally {
      setEnvoi(false);
      setProgres(0);
    }
  };

  /** Suit le travail déposé pour afficher où il en est, plan par plan. */
  const suivre = (tacheId: number) => {
    const minuteur = setInterval(async () => {
      try {
        const tache = await assistantsService.suivreTache(tacheId);
        setEtape(tache.avancement || 'Transcription en cours');
        if (tache.etat === 'terminee' || tache.etat === 'echouee') {
          clearInterval(minuteur);
          setEtape('');
          if (tache.etat === 'echouee') setErreur(tache.erreur || 'La transcription a échoué.');
          await recharger();
        }
      } catch {
        clearInterval(minuteur);
        setEtape('');
      }
    }, CADENCE);
  };

  const dateLisible = (valeur: string | null) =>
    (valeur
      ? new Date(valeur).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
      : '');

  if (ouverte) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <div>
            <button
              type="button"
              onClick={() => { setOuverte(null); recharger().catch(() => {}); }}
              className="text-sm text-eg-accent hover:underline"
            >
              ← Toutes les réunions
            </button>
            <div className="mt-2 flex items-start justify-between gap-4">
              <h1 className="text-2xl font-bold text-eg-encre">
                {ouverte.titre || `Réunion du ${dateLisible(ouverte.deposee_le)}`}
              </h1>
              <button
                type="button"
                onClick={() => setASupprimer(
                  aSupprimer === ouverte.id ? null : ouverte.id,
                )}
                className="inline-flex shrink-0 items-center gap-2 rounded-md border
                  border-[#DDE2DD] px-3 py-1.5 text-sm text-eg-gris hover:text-[#8A2A2A]"
              >
                <Trash2 className="w-4 h-4" /> Supprimer
              </button>
            </div>
            <p className="mt-1 text-sm text-eg-gris">
              {ouverte.projet_nom ? `Activité « ${ouverte.projet_nom} »` : 'Sans activité'}
              {' '}— déposée par {ouverte.deposee_par}
              {ouverte.tenue_le ? `, tenue le ${dateLisible(ouverte.tenue_le)}` : ''}
            </p>
          </div>

          {aSupprimer === ouverte.id && (
            <div className="rounded-md border border-[#E4B9B9] bg-[#FBF1F1] px-4 py-3">
              <p className="text-sm text-[#8A2A2A]">
                Supprimer définitivement cette réunion ? L'enregistrement et la
                transcription seront effacés
                {ouverte.versee
                  ? ", ainsi que le compte rendu versé aux documents de l'activité, "
                    + "que l'assistant documentaire ne pourra donc plus citer."
                  : '.'}
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => supprimer(ouverte.id)}
                  disabled={suppression}
                  className="inline-flex items-center gap-2 rounded-md bg-[#8A2A2A] px-3 py-1.5
                    text-sm font-medium text-white disabled:opacity-50"
                >
                  {suppression && <Loader2 className="w-4 h-4 animate-spin" />}
                  Supprimer définitivement
                </button>
                <button
                  type="button"
                  onClick={() => setASupprimer(null)}
                  className="rounded-md border border-[#DDE2DD] bg-white px-3 py-1.5
                    text-sm text-eg-encre"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {erreur && (
            <div className="flex items-start gap-2 rounded-md border border-[#E4B9B9]
              bg-[#FBF1F1] px-4 py-3 text-sm text-[#8A2A2A]">
              <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
              {erreur}
            </div>
          )}

          <RelectureReunion
            reunion={ouverte}
            projets={projets}
            onValidee={(majour) => {
              setOuverte(majour);
              recharger().catch(() => {});
            }}
          />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <div className="flex items-center gap-3 text-eg-accent">
            <AudioLines className="w-6 h-6" />
            <span className="text-sm font-semibold uppercase tracking-wide">Restitution</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-eg-encre">Réunions</h1>
          <p className="mt-2 text-eg-gris">
            Déposez l'enregistrement d'une réunion : la plateforme en établit un compte rendu
            structuré que vous relisez avant de le verser aux documents de l'activité.
          </p>
        </div>

        <Card className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <div className="flex items-center gap-4">
                <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                  Enregistrement
                </label>
                <div className="flex gap-1 rounded-md bg-eg-voile p-0.5">
                  {([['micro', 'Enregistrer'], ['fichier', 'Déposer un fichier']] as const)
                    .map(([valeur, libelle]) => (
                      <button
                        key={valeur}
                        type="button"
                        onClick={() => { setSource(valeur); setFichier(null); }}
                        className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                          source === valeur
                            ? 'bg-white text-eg-encre shadow-sm'
                            : 'text-eg-gris hover:text-eg-encre'
                        }`}
                      >
                        {libelle}
                      </button>
                    ))}
                </div>
              </div>

              <div className="mt-2">
                {source === 'micro' ? (
                  <EnregistreurReunion
                    key={remise}
                    onEnregistre={setFichier}
                    desactive={envoi}
                  />
                ) : (
                  <>
                    <input
                      ref={champ}
                      type="file"
                      accept="audio/*,video/*,.m4a,.mp3,.wav,.ogg,.opus,.amr,.aac,.mp4,.3gp"
                      onChange={(e) => setFichier(e.target.files?.[0] || null)}
                      className="w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                        file:mr-3 file:rounded file:border-0 file:bg-eg-voile file:px-3 file:py-1
                        file:text-sm file:text-eg-ombre"
                    />
                    {/* Les formats acceptés étaient connus du seul champ de
                        dépôt. Un dépôt refusé laissait donc chercher au
                        hasard ce qui n'allait pas. */}
                    <p className="mt-1 text-xs text-eg-estompe">
                      Formats acceptés : m4a, mp3, wav, ogg, opus, amr, aac, ainsi que
                      les vidéos mp4 et 3gp, dont seul le son est conservé. Deux cents
                      mégaoctets au plus.
                    </p>
                  </>
                )}
              </div>

              {fichier && (
                <p className="mt-1 text-xs text-eg-estompe">
                  {enOctetsLisibles(fichier.size)} — allégé avant l'envoi quand c'est possible.
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                Activité (facultative)
              </label>
              <div className="mt-1">
                <ChoixActivite
                  projets={projets}
                  valeur={projet}
                  onChange={setProjet}
                  optionVide="Ranger plus tard"
                  desactive={envoi}
                />
              </div>
              <p className="mt-1 text-xs text-eg-estompe">
                Vous pourrez la choisir au moment de valider le compte rendu.
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                Objet (facultatif)
              </label>
              <input
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="Ex. : Revue mensuelle du PEV — Kankan"
                className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                  focus:border-eg-accent focus:outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-eg-gris">
                Date de la réunion (facultatif)
              </label>
              <input
                type="date"
                value={tenueLe}
                onChange={(e) => setTenueLe(e.target.value)}
                className="mt-1 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                  focus:border-eg-accent focus:outline-none"
              />
            </div>
          </div>

          <div className="rounded-xl bg-eg-papier px-4 py-3.5">
            <p className="text-[13.5px] font-medium text-eg-encre">
              Avez-vous un plan particulier de compte rendu ?
            </p>
            <p className="mt-0.5 text-xs text-eg-gris">
              Sinon, le compte rendu suit le plan standard : résumé, points abordés,
              décisions et actions, chacun adossé à un extrait de l'enregistrement.
            </p>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {([
                ['non', 'Non, plan standard'],
                ['texte', 'Oui, je le saisis'],
                ['fichier', "Oui, j'ai un fichier"],
              ] as const).map(([valeur, libelle]) => (
                <button
                  key={valeur}
                  type="button"
                  disabled={envoi}
                  onClick={() => setAvecPlan(valeur)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    avecPlan === valeur
                      ? 'bg-eg-ombre text-white'
                      : 'bg-eg-blanc text-eg-brun outline outline-1 -outline-offset-1 outline-black/[0.08] hover:bg-eg-voile'
                  }`}
                >
                  {libelle}
                </button>
              ))}
            </div>
            {avecPlan === 'texte' && (
              <div className="mt-3">
                <textarea
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  rows={5}
                  disabled={envoi}
                  placeholder={'Une rubrique par ligne, par exemple :\n1. Situation des stocks\n2. Couverture du mois\n3. Supervision\n4. Divers'}
                  className="w-full rounded-lg border border-eg-bord bg-eg-blanc px-3 py-2 text-sm
                    placeholder:text-eg-estompe focus:border-eg-accent focus:outline-none"
                />
                {dernierPlan && !plan && (
                  <button
                    type="button"
                    onClick={() => setPlan(dernierPlan)}
                    className="mt-1 text-xs font-medium text-eg-accent hover:underline"
                  >
                    Reprendre mon dernier plan
                  </button>
                )}
              </div>
            )}
            {avecPlan === 'fichier' && (
              <div className="mt-3">
                <input
                  type="file"
                  accept=".docx,.pdf,.txt,.md"
                  disabled={envoi}
                  onChange={(e) => setPlanFichier(e.target.files?.[0] || null)}
                  className="w-full rounded-lg border border-eg-bord bg-eg-blanc px-3 py-2 text-sm
                    file:mr-3 file:rounded-full file:border-0 file:bg-eg-voile file:px-3 file:py-1
                    file:text-xs file:text-eg-encre"
                />
                <p className="mt-1 text-xs text-eg-estompe">
                  Word, PDF ou texte. Les intitulés du document deviennent les rubriques.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-eg-estompe">
              Informez les participants de l'enregistrement avant de le déposer.
            </p>
            <button
              type="button"
              onClick={deposer}
              disabled={
                !fichier || envoi
                || (avecPlan === 'texte' && !plan.trim())
                || (avecPlan === 'fichier' && !planFichier)
              }
              className="inline-flex items-center gap-2 rounded-md bg-eg-ombre px-4 py-2
                text-sm font-medium text-white disabled:opacity-50"
            >
              {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Déposer
            </button>
          </div>

          {etape && (
            <div className="rounded-md border border-[#DDE2DD] bg-eg-voile px-3 py-2">
              <div className="flex items-center gap-2 text-sm text-eg-encre">
                <Loader2 className="w-4 h-4 animate-spin text-eg-accent" />
                {etape}
              </div>
              {envoi && progres > 0 && (
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-white">
                  <div
                    className="h-full bg-eg-accent transition-all"
                    style={{ width: `${Math.round(progres * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {erreur && (
            <div className="flex items-start gap-2 rounded-md border border-[#E4B9B9]
              bg-[#FBF1F1] px-3 py-2 text-sm text-[#8A2A2A]">
              <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
              {erreur}
            </div>
          )}
        </Card>

        {chargement ? (
          <Card className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-eg-estompe" />
          </Card>
        ) : reunions.length === 0 ? (
          <Card className="p-6 text-sm text-eg-gris">
            Aucune réunion déposée. Le compte rendu que vous validerez deviendra un document
            de l'activité, que l'assistant documentaire pourra citer.
          </Card>
        ) : (
          <div className="space-y-2">
            {reunions.map((reunion) => {
              const etat = ETATS[reunion.etat];
              const relisible = reunion.etat === 'a_relire' || reunion.etat === 'validee';
              return (
                <div
                  key={reunion.id}
                  className="rounded-md border border-[#E9ECE9] bg-white p-4
                    transition-colors hover:border-eg-accent"
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      disabled={!relisible}
                      onClick={() => ouvrir(reunion)}
                      className={`min-w-0 flex-1 text-left ${relisible ? '' : 'cursor-default'}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-eg-encre">
                          {reunion.titre || `Réunion du ${dateLisible(reunion.deposee_le)}`}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${etat.classe}`}>
                          {etat.libelle}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-eg-gris">
                        {reunion.projet_nom || 'Sans activité'} — déposée par{' '}
                        {reunion.deposee_par} le {dateLisible(reunion.deposee_le)}
                      </p>
                      {reunion.etat === 'echouee' && reunion.erreur && (
                        <p className="mt-1 text-xs text-[#8A2A2A]">{reunion.erreur}</p>
                      )}
                      {reunion.etat === 'a_relire' && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-eg-accent">
                          <Clock className="w-3 h-3" /> En attente de votre relecture
                        </p>
                      )}
                      {reunion.versee && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-[#1F5C45]">
                          <CheckCircle2 className="w-3 h-3" /> Versée aux documents de l'activité
                        </p>
                      )}
                    </button>

                    <button
                      type="button"
                      title="Supprimer cette réunion"
                      onClick={() => setASupprimer(
                        aSupprimer === reunion.id ? null : reunion.id,
                      )}
                      className="shrink-0 rounded p-1.5 text-eg-estompe hover:text-[#8A2A2A]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {aSupprimer === reunion.id && (
                    <div className="mt-3 rounded-md border border-[#E4B9B9] bg-[#FBF1F1] px-3 py-3">
                      <p className="text-sm text-[#8A2A2A]">
                        Supprimer définitivement cette réunion ? L'enregistrement et la
                        transcription seront effacés
                        {reunion.versee
                          ? ", ainsi que le compte rendu versé aux documents de l'activité, "
                            + "que l'assistant documentaire ne pourra donc plus citer."
                          : '.'}
                      </p>
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => supprimer(reunion.id)}
                          disabled={suppression}
                          className="inline-flex items-center gap-2 rounded-md bg-[#8A2A2A]
                            px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                        >
                          {suppression && <Loader2 className="w-4 h-4 animate-spin" />}
                          Supprimer définitivement
                        </button>
                        <button
                          type="button"
                          onClick={() => setASupprimer(null)}
                          className="rounded-md border border-[#DDE2DD] bg-white px-3 py-1.5
                            text-sm text-eg-encre"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReunionsPage;
